import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import LocationAutocomplete from "../components/LocationAutocomplete";
import { CountryAutocomplete, CityAutocomplete } from "../components/CountryCityAutocomplete";
import { SmartLocationField } from "../components/SmartLocationInput";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Ship,
  Package,
  Plane,
  Truck,
  Layers,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  Send,
  CheckCircle,
  Clock,
  FileText,
  Building2,
  MapPin,
  Calendar,
  Weight,
  Box,
  Shield,
  Globe,
  ArrowRight,
  AlertCircle,
  X,
  User,
  Mail,
  Phone,
  Anchor,
  Save,
  Loader2,
  Scale,
  ClipboardList,
  Upload,
  File,
  AlertTriangle,
} from "lucide-react";


const SERVICE_TYPES = [
  {
    id: "fcl",
    name: "FCL",
    fullName: "Full Container Load",
    description: "Book entire containers for your cargo",
    icon: Ship,
    color: "from-blue-500 to-blue-600",
  },
  {
    id: "lcl",
    name: "LCL",
    fullName: "Less than Container Load",
    description: "Share container space with other shippers",
    icon: Package,
    color: "from-emerald-500 to-emerald-600",
  },
  {
    id: "air",
    name: "Air Freight",
    fullName: "Air Cargo",
    description: "Fast delivery via air transport",
    icon: Plane,
    color: "from-violet-500 to-violet-600",
  },
  {
    id: "inland",
    name: "Inland",
    fullName: "Road & Rail Transport",
    description: "Trucking and rail services",
    icon: Truck,
    color: "from-amber-500 to-amber-600",
  },
  {
    id: "multimodal",
    name: "Multimodal",
    fullName: "Combined Transport",
    description: "Sea, air, road, and rail combined",
    icon: Layers,
    color: "from-rose-500 to-rose-600",
  },
];

const CONTAINER_TYPES = [
  { value: "20GP", label: "20' Standard (20GP)" },
  { value: "40GP", label: "40' Standard (40GP)" },
  { value: "40HC", label: "40' High Cube (40HC)" },
  { value: "45HC", label: "45' High Cube (45HC)" },
  { value: "20RF", label: "20' Reefer (20RF)" },
  { value: "40RF", label: "40' Reefer (40RF)" },
  { value: "40RHC", label: "40' HC Reefer (40RHC)" },
  { value: "20OT", label: "20' Open Top (20OT)" },
  { value: "40OT", label: "40' Open Top (40OT)" },
  { value: "20FR", label: "20' Flat Rack (20FR)" },
  { value: "40FR", label: "40' Flat Rack (40FR)" },
  { value: "20PL", label: "20' Platform (20PL)" },
  { value: "40PL", label: "40' Platform (40PL)" },
  { value: "TANK", label: "ISO Tank Container" },
];

const INCOTERMS = [
  { value: "EXW", label: "EXW - Ex Works" },
  { value: "FCA", label: "FCA - Free Carrier" },
  { value: "FAS", label: "FAS - Free Alongside Ship" },
  { value: "FOB", label: "FOB - Free on Board" },
  { value: "CFR", label: "CFR - Cost and Freight" },
  { value: "CIF", label: "CIF - Cost, Insurance & Freight" },
  { value: "CPT", label: "CPT - Carriage Paid To" },
  { value: "CIP", label: "CIP - Carriage & Insurance Paid" },
  { value: "DAP", label: "DAP - Delivered at Place" },
  { value: "DPU", label: "DPU - Delivered at Place Unloaded" },
  { value: "DDP", label: "DDP - Delivered Duty Paid" },
];

const PACKAGE_TYPES = [
  "Carton", "Pallet", "Crate", "Drum", "Bag", "Bundle", "Roll", "Coil", "Container", "Other"
];

// Document types for quote requests
const DOCUMENT_TYPES = [
  { value: "commercial_invoice", label: "Commercial Invoice", description: "Invoice for the goods being shipped" },
  { value: "packing_list", label: "Packing List", description: "Detailed list of package contents" },
  { value: "msds", label: "MSDS / SDS", description: "Material Safety Data Sheet for dangerous goods" },
  { value: "certificate_of_origin", label: "Certificate of Origin", description: "Document certifying country of manufacture" },
  { value: "export_license", label: "Export License", description: "License for controlled goods" },
  { value: "technical_specs", label: "Technical Specifications", description: "Product technical documentation" },
  { value: "other", label: "Other Document", description: "Any other supporting document" },
];

const emptyCargoItem = {
  description: "",
  package_type: "Carton",
  quantity: 1,
  weight_kg: 0,
  length_cm: "",
  width_cm: "",
  height_cm: "",
  volume_cbm: "",
  is_hazardous: false,
  un_number: "",
  hs_code: "",
};

const emptyAdditionalServices = {
  pre_carriage: false,
  pre_carriage_address: "",
  pre_carriage_city: "",
  pre_carriage_country: "",
  on_carriage: false,
  on_carriage_address: "",
  on_carriage_city: "",
  on_carriage_country: "",
  customs_clearance: false,
  customs_type: "",
  insurance: false,
  insurance_value: "",
  insurance_currency: "EUR",
  warehousing: false,
  packaging: false,
  fumigation: false,
};

export default function QuotesPage() {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState("");
  
  // Draft management
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(true);
  
  // Document uploads
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // Routing - unified location objects
    origin: {
      city: "",
      country: "",
      port_code: null,
      port_name: null,
      airport_code: null,
      airport_name: null,
      location_type: null,
    },
    origin_address: "",
    destination: {
      city: "",
      country: "",
      port_code: null,
      port_name: null,
      airport_code: null,
      airport_name: null,
      location_type: null,
    },
    destination_address: "",
    
    // Stopovers (multi-stop routing)
    stopovers: [],
    
    // FCL specific
    container_type: "40HC",
    container_quantity: 1,
    cargo_entry_mode: "simple", // 'simple' or 'detailed'
    piece_count: "",
    simple_package_type: "carton",
    
    // Cargo
    cargo_items: [{ ...emptyCargoItem }],
    total_weight_kg: "",
    total_volume_cbm: "",
    commodity: "",
    cargo_ready_date: "",
    
    // Additional services
    additional_services: { ...emptyAdditionalServices },
    incoterm: "",
    
    // Contact
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    
    // Notes
    special_requirements: "",
  });

  // Empty stopover template
  const emptyStopover = {
    country: "",
    city: "",
    address: "",
    service_type: "customs", // customs, loading, unloading, other
    service_description: "",
  };

  const token = localStorage.getItem("token");

  // Fetch available drafts on load
  useEffect(() => {
    const fetchDrafts = async () => {
      if (!token) return;
      setLoadingDrafts(true);
      try {
        const response = await axios.get(`${API_URL}/quote-drafts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data?.drafts?.length > 0) {
          setAvailableDrafts(response.data.drafts);
        }
      } catch (error) {
        console.error("Failed to fetch drafts");
      } finally {
        setLoadingDrafts(false);
      }
    };
    fetchDrafts();
  }, [token]);

  // Pre-fill contact info if logged in
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setFormData(prev => ({
            ...prev,
            company_name: prev.company_name || response.data.company_name || "",
            contact_name: prev.contact_name || response.data.full_name || "",
            email: prev.email || response.data.email || "",
          }));
        }
      } catch (error) {
        console.error("Failed to fetch user info");
      }
    };
    fetchUserInfo();
  }, [token]);
  
  // Load a saved draft
  const loadDraft = (draft) => {
    if (draft.service_type) {
      setSelectedService(draft.service_type);
    }
    if (draft.current_step) {
      setStep(draft.current_step);
    }
    if (draft.form_data) {
      setFormData(prev => ({
        ...prev,
        ...draft.form_data,
        // Ensure nested objects are properly merged
        additional_services: {
          ...emptyAdditionalServices,
          ...(draft.form_data.additional_services || {})
        },
        cargo_items: draft.form_data.cargo_items?.length > 0 
          ? draft.form_data.cargo_items 
          : [{ ...emptyCargoItem }],
        stopovers: draft.form_data.stopovers || []
      }));
    }
    setShowDraftBanner(false);
    toast.success("Draft loaded! Continue where you left off.");
  };
  
  // Stopover management functions
  const addStopover = () => {
    setFormData(prev => ({
      ...prev,
      stopovers: [...prev.stopovers, { ...emptyStopover }]
    }));
  };
  
  const removeStopover = (index) => {
    setFormData(prev => ({
      ...prev,
      stopovers: prev.stopovers.filter((_, i) => i !== index)
    }));
  };
  
  const updateStopover = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      stopovers: prev.stopovers.map((stop, i) => 
        i === index ? { ...stop, [field]: value } : stop
      )
    }));
  };
  
  // Delete a draft
  const deleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API_URL}/quote-drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success("Draft deleted");
    } catch (error) {
      toast.error("Failed to delete draft");
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAdditionalServices = (field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_services: { ...prev.additional_services, [field]: value }
    }));
  };

  const addCargoItem = () => {
    setFormData(prev => ({
      ...prev,
      cargo_items: [...prev.cargo_items, { ...emptyCargoItem }]
    }));
  };

  const removeCargoItem = (index) => {
    setFormData(prev => ({
      ...prev,
      cargo_items: prev.cargo_items.filter((_, i) => i !== index)
    }));
  };

  const updateCargoItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cargo_items: prev.cargo_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotals = useCallback(() => {
    const items = formData.cargo_items;
    const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.weight_kg) || 0) * (item.quantity || 1), 0);
    const totalVolume = items.reduce((sum, item) => {
      if (item.volume_cbm) return sum + (parseFloat(item.volume_cbm) || 0) * (item.quantity || 1);
      if (item.length_cm && item.width_cm && item.height_cm) {
        const cbm = (parseFloat(item.length_cm) * parseFloat(item.width_cm) * parseFloat(item.height_cm)) / 1000000;
        return sum + cbm * (item.quantity || 1);
      }
      return sum;
    }, 0);
    return { totalWeight, totalVolume };
  }, [formData.cargo_items]);

  const [savingDraft, setSavingDraft] = useState(false);

  const handleSaveDraft = async () => {
    if (!token) {
      toast.error("Please sign in to save drafts");
      return;
    }
    
    setSavingDraft(true);
    try {
      const draftPayload = {
        service_type: selectedService,
        current_step: step,
        form_data: formData,
        last_saved: new Date().toISOString()
      };
      
      await axios.post(`${API_URL}/quote-drafts`, draftPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Draft saved! We'll remind you to complete it.");
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg).join(', ') || "Failed to save draft");
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : "Failed to save draft");
      }
    } finally {
      setSavingDraft(false);
    }
  };

  // Document upload handlers
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PDF, images, or Office documents.");
      return;
    }

    setUploadingDoc(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', docType);
      formDataUpload.append('original_filename', file.name);

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API_URL}/quote-documents/upload`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      const newDoc = {
        id: response.data.document_id,
        type: docType,
        filename: file.name,
        size: file.size,
        uploaded_at: new Date().toISOString()
      };

      setDocuments(prev => [...prev, newDoc]);
      toast.success(`${DOCUMENT_TYPES.find(d => d.value === docType)?.label || 'Document'} uploaded successfully`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
      // Reset the input
      e.target.value = '';
    }
  };

  const handleRemoveDocument = (docId) => {
    setDocuments(prev => prev.filter(d => d.id !== docId));
    // Note: We'll clean up orphaned uploads on the backend
  };

  const handleSubmit = async () => {
    // Validation - check all required fields
    if (!formData.origin.city) {
      toast.error("Please fill in origin location");
      return;
    }
    if (!formData.destination.city) {
      toast.error("Please fill in destination location");
      return;
    }
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error("Please fill in contact details");
      return;
    }

    setSubmitting(true);
    try {
      const { totalWeight, totalVolume } = calculateTotals();
      
      // Flatten location objects for the API
      const payload = {
        service_type: selectedService,
        origin_country: formData.origin.country || "",
        origin_city: formData.origin.city,
        origin_port_airport: formData.origin.port_code || formData.origin.port_name || "",
        origin_address: formData.origin_address,
        destination_country: formData.destination.country || "",
        destination_city: formData.destination.city,
        destination_port_airport: formData.destination.port_code || formData.destination.port_name || "",
        destination_address: formData.destination_address,
        stopovers: formData.stopovers.map(s => ({
          ...s,
          port_code: s.port_code || null,
        })),
        container_type: formData.container_type,
        total_weight_kg: formData.total_weight_kg ? parseFloat(formData.total_weight_kg) : (totalWeight || null),
        total_volume_cbm: formData.total_volume_cbm ? parseFloat(formData.total_volume_cbm) : (totalVolume || null),
        container_quantity: formData.container_quantity ? parseInt(formData.container_quantity, 10) : 1,
        cargo_items: formData.cargo_items
          .filter(item => item.description)
          .map(item => ({
            ...item,
            quantity: item.quantity ? parseInt(item.quantity, 10) : 1,
            weight_kg: item.weight_kg ? parseFloat(item.weight_kg) : null,
            length_cm: item.length_cm ? parseFloat(item.length_cm) : null,
            width_cm: item.width_cm ? parseFloat(item.width_cm) : null,
            height_cm: item.height_cm ? parseFloat(item.height_cm) : null,
          })),
        // Include uploaded documents
        document_ids: documents.map(d => d.id),
      };

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API_URL}/quotes`, payload, { headers });
      
      setQuoteNumber(response.data.quote_number);
      setSubmitted(true);
      toast.success("Quote request submitted successfully!");
      
      // Delete any draft for this service type after successful submission
      if (token && selectedService) {
        const matchingDraft = availableDrafts.find(d => d.service_type === selectedService);
        if (matchingDraft) {
          try {
            await axios.delete(`${API_URL}/quote-drafts/${matchingDraft.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableDrafts(prev => prev.filter(d => d.id !== matchingDraft.id));
          } catch (e) {
            // Ignore draft deletion errors
          }
        }
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) vs string errors
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => e.msg || e.message || 'Validation error').join(', ');
        toast.error(messages || "Validation failed");
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (typeof errorDetail === 'object' && errorDetail?.msg) {
        toast.error(errorDetail.msg);
      } else {
        toast.error("Failed to submit quote request");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedService(null);
    setSubmitted(false);
    setQuoteNumber("");
    setFormData({
      origin_country: "",
      origin_city: "",
      origin_address: "",
      origin_port_airport: "",
      destination_country: "",
      destination_city: "",
      destination_address: "",
      destination_port_airport: "",
      container_type: "40HC",
      container_quantity: 1,
      cargo_items: [{ ...emptyCargoItem }],
      total_weight_kg: "",
      total_volume_cbm: "",
      commodity: "",
      cargo_ready_date: "",
      additional_services: { ...emptyAdditionalServices },
      incoterm: "",
      company_name: "",
      contact_name: "",
      email: "",
      phone: "",
      special_requirements: "",
    });
  };

  // Render service selection
  const renderServiceSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Choose Your Service</h2>
        <p className="text-muted-foreground">Select the type of freight service you need</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICE_TYPES.map((service) => {
          const Icon = service.icon;
          const isSelected = selectedService === service.id;
          return (
            <motion.button
              key={service.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedService(service.id)}
              data-testid={`service-${service.id}`}
              className={`relative p-6 rounded-lg border-2 text-left transition-all ${
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50 bg-surface"
              }`}
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center mb-4`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">{service.name}</h3>
              <p className="text-sm text-muted-foreground mb-2">{service.fullName}</p>
              <p className="text-xs text-muted-foreground">{service.description}</p>
              {isSelected && (
                <div className="absolute top-4 right-4">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      <div className="flex justify-end mt-8">
        <Button
          onClick={() => setStep(2)}
          disabled={!selectedService}
          data-testid="continue-to-routing"
          className="gap-2"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Render routing step
  const renderRoutingStep = () => {
    const isAir = selectedService === "air";
    const portLabel = isAir ? "Airport" : "Seaport";
    const portType = isAir ? "air" : "sea";
    const isTrucking = selectedService === "trucking";
    
    // Service type options for stopovers
    const serviceTypes = [
      { value: "customs", label: "Customs Stop" },
      { value: "loading", label: "Loading" },
      { value: "unloading", label: "Unloading" },
      { value: "inspection", label: "Inspection" },
      { value: "warehouse", label: "Warehouse" },
      { value: "other", label: "Other" },
    ];
    
    return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Routing Details</h2>
        <p className="text-muted-foreground">
          {isTrucking 
            ? "Define your route with optional stopovers (A → B → C)" 
            : "Where is your cargo coming from and going to?"}
        </p>
      </div>

      {/* Route visualization */}
      {(formData.origin.city || formData.stopovers.length > 0 || formData.destination.city) && (
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-center flex-wrap gap-2 text-sm">
            <span className="font-medium text-emerald-700 bg-white px-3 py-1 rounded-full shadow-sm flex items-center gap-1">
              {formData.origin.city || "Origin"}
              {formData.origin.port_code && (
                <span className="text-xs text-emerald-500 font-mono">({formData.origin.port_code})</span>
              )}
            </span>
            {formData.stopovers.map((stop, idx) => (
              <span key={idx} className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="font-medium text-orange-600 bg-white px-3 py-1 rounded-full shadow-sm">
                  {stop.city || `Stop ${idx + 1}`}
                  {stop.service_type && (
                    <span className="text-xs text-slate-500 ml-1">({stop.service_type})</span>
                  )}
                </span>
              </span>
            ))}
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className="font-medium text-blue-700 bg-white px-3 py-1 rounded-full shadow-sm flex items-center gap-1">
              {formData.destination.city || "Destination"}
              {formData.destination.port_code && (
                <span className="text-xs text-blue-500 font-mono">({formData.destination.port_code})</span>
              )}
            </span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Origin */}
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-6 shadow-sm relative">
          <div className="absolute -top-3 left-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            START
          </div>
          <div className="flex items-center gap-2 mb-6 pt-2">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Origin</h3>
              <p className="text-sm text-slate-500">Where the cargo starts</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Smart Location Input - combines city, country, and port */}
            <SmartLocationField
              label="City / Port"
              value={formData.origin}
              onChange={(location) => updateFormData("origin", location)}
              showSeaport={!isTrucking && !isAir}
              showAirport={isAir}
              required
              data-testid="origin-location"
            />
            
            <div>
              <Label className="text-slate-700">Pickup Address</Label>
              <Input
                value={formData.origin_address}
                onChange={(e) => updateFormData("origin_address", e.target.value)}
                placeholder="Full address for collection (optional)"
                data-testid="origin-address"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Stopovers */}
        {formData.stopovers.map((stopover, index) => (
          <div key={index} className="bg-white border-2 border-orange-200 rounded-xl p-6 shadow-sm relative">
            <div className="absolute -top-3 left-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              STOP {index + 1}
            </div>
            <button
              onClick={() => removeStopover(index)}
              className="absolute -top-3 right-4 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
              title="Remove stopover"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-center gap-2 mb-6 pt-2">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Stopover {index + 1}</h3>
                <p className="text-sm text-slate-500">Intermediate stop on the route</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Smart Location Input for stopovers */}
              <SmartLocationField
                label="City / Port"
                value={{
                  city: stopover.city,
                  country: stopover.country,
                  port_code: stopover.port_code,
                  port_name: stopover.port_name,
                }}
                onChange={(location) => {
                  const newStopovers = [...formData.stopovers];
                  newStopovers[index] = {
                    ...newStopovers[index],
                    city: location.city,
                    country: location.country,
                    port_code: location.port_code,
                    port_name: location.port_name,
                  };
                  setFormData(prev => ({ ...prev, stopovers: newStopovers }));
                }}
                showSeaport={!isTrucking && !isAir}
                showAirport={isAir}
                required
                data-testid={`stopover-${index}-location`}
              />
              
              <div>
                <Label className="text-slate-700">Address</Label>
                <Input
                  value={stopover.address}
                  onChange={(e) => updateStopover(index, "address", e.target.value)}
                  placeholder="Full address (optional)"
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700 font-medium">
                    Service Required <span className="text-red-500">*</span>
                  </Label>
                  <select
                    value={stopover.service_type}
                    onChange={(e) => updateStopover(index, "service_type", e.target.value)}
                    className="w-full mt-1 h-10 px-3 rounded-md border border-orange-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {serviceTypes.map(st => (
                      <option key={st.value} value={st.value}>{st.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700">Service Details</Label>
                  <Input
                    value={stopover.service_description}
                    onChange={(e) => updateStopover(index, "service_description", e.target.value)}
                    placeholder="Additional details..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Add Stopover Button */}
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={addStopover}
            className="gap-2 border-dashed border-2 border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            <Plus className="w-4 h-4" />
            Add Stopover
          </Button>
        </div>

        {/* Destination */}
        <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm relative">
          <div className="absolute -top-3 left-4 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            END
          </div>
          <div className="flex items-center gap-2 mb-6 pt-2">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Destination</h3>
              <p className="text-sm text-slate-500">Where the cargo is going</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Smart Location Input - combines city, country, and port */}
            <SmartLocationField
              label="City / Port"
              value={formData.destination}
              onChange={(location) => updateFormData("destination", location)}
              showSeaport={!isTrucking && !isAir}
              showAirport={isAir}
              required
              data-testid="dest-location"
            />
            
            <div>
              <Label className="text-slate-700">Delivery Address</Label>
              <Input
                value={formData.destination_address}
                onChange={(e) => updateFormData("destination_address", e.target.value)}
                placeholder="Full address for delivery (optional)"
                data-testid="dest-address"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Required fields note */}
      <div className="bg-slate-50 rounded-lg p-4 mt-6">
        <p className="text-sm text-slate-600 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          Fields marked with <span className="text-red-500 font-bold">*</span> are required
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={() => {
            // Validate required fields
            if (!formData.origin.city) {
              toast.error("Please fill in origin location");
              return;
            }
            if (!formData.destination.city) {
              toast.error("Please fill in destination location");
              return;
            }
            // Validate stopovers
            for (let i = 0; i < formData.stopovers.length; i++) {
              const stop = formData.stopovers[i];
              if (!stop.city) {
                toast.error(`Please fill in location for stopover ${i + 1}`);
                return;
              }
            }
            setStep(3);
          }}
          className="gap-2"
          data-testid="continue-to-cargo"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )};

  // Render cargo step
  const renderCargoStep = () => {
    const { totalWeight, totalVolume } = calculateTotals();
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-2">Cargo Details</h2>
          <p className="text-muted-foreground">Describe your shipment</p>
        </div>

        {/* FCL-specific options */}
        {selectedService === "fcl" && (
          <div className="bg-surface border border-border rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Ship className="w-5 h-5 text-primary" />
              Container Requirements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Container Type</Label>
                <select
                  value={formData.container_type}
                  onChange={(e) => updateFormData("container_type", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-border bg-background text-foreground"
                  data-testid="container-type"
                >
                  {CONTAINER_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Number of Containers</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.container_quantity}
                  onChange={(e) => updateFormData("container_quantity", parseInt(e.target.value) || 1)}
                  data-testid="container-quantity"
                />
              </div>
            </div>
          </div>
        )}

        {/* FCL Cargo Entry Mode Toggle */}
        {selectedService === "fcl" && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-sm font-medium text-slate-600">How would you like to enter cargo details?</span>
            </div>
            <div className="flex justify-center gap-2">
              <Button
                type="button"
                variant={formData.cargo_entry_mode === 'simple' ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateFormData("cargo_entry_mode", "simple")}
                className="gap-2"
                data-testid="cargo-mode-simple"
              >
                <Package className="w-4 h-4" />
                Simple (Totals Only)
              </Button>
              <Button
                type="button"
                variant={formData.cargo_entry_mode !== 'simple' ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateFormData("cargo_entry_mode", "detailed")}
                className="gap-2"
                data-testid="cargo-mode-detailed"
              >
                <ClipboardList className="w-4 h-4" />
                Detailed (Item List)
              </Button>
            </div>
          </div>
        )}

        {/* SIMPLE MODE - Total Weight & Pieces per Container */}
        {selectedService === "fcl" && formData.cargo_entry_mode === 'simple' && (
          <div className="bg-surface border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              Cargo Summary (Per Container)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enter the total weight and piece count for each container
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Weight className="w-4 h-4 text-slate-500" />
                  Total Weight (kg) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="e.g., 15000"
                  value={formData.total_weight_kg}
                  onChange={(e) => updateFormData("total_weight_kg", e.target.value)}
                  className="mt-1"
                  data-testid="simple-total-weight"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Total weight of cargo per container
                </p>
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Layers className="w-4 h-4 text-slate-500" />
                  Number of Pieces <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 250"
                  value={formData.piece_count}
                  onChange={(e) => updateFormData("piece_count", e.target.value)}
                  className="mt-1"
                  data-testid="simple-piece-count"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Total number of packages/pieces
                </p>
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Box className="w-4 h-4 text-slate-500" />
                  Package Type
                </Label>
                <select
                  value={formData.simple_package_type || "carton"}
                  onChange={(e) => updateFormData("simple_package_type", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-border bg-background text-foreground mt-1"
                  data-testid="simple-package-type"
                >
                  <option value="carton">Carton</option>
                  <option value="pallet">Pallet</option>
                  <option value="crate">Crate</option>
                  <option value="drum">Drum</option>
                  <option value="bag">Bag</option>
                  <option value="bundle">Bundle</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Summary Display */}
            {formData.container_quantity > 0 && formData.total_weight_kg && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-700 font-medium">Total Shipment Summary:</span>
                  <div className="flex gap-4">
                    <span className="text-blue-900">
                      <strong>{formData.container_quantity}</strong> container(s)
                    </span>
                    <span className="text-blue-900">
                      <strong>{(parseFloat(formData.total_weight_kg) * formData.container_quantity).toLocaleString()}</strong> kg total
                    </span>
                    {formData.piece_count && (
                      <span className="text-blue-900">
                        <strong>{(parseInt(formData.piece_count) * formData.container_quantity).toLocaleString()}</strong> pieces total
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DETAILED MODE - Cargo Items (existing functionality) */}
        {(selectedService !== "fcl" || formData.cargo_entry_mode !== 'simple') && (
          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Box className="w-5 h-5 text-primary" />
                Cargo Items
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={addCargoItem}
                data-testid="add-cargo-item"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </div>

            <div className="space-y-4">
              {formData.cargo_items.map((item, index) => (
                <div key={index} className="border border-border rounded-lg p-4 bg-background">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-muted-foreground">Item {index + 1}</span>
                    {formData.cargo_items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCargoItem(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateCargoItem(index, "description", e.target.value)}
                      placeholder="e.g., Electronic components"
                      data-testid={`cargo-description-${index}`}
                    />
                  </div>
                  <div>
                    <Label>Package Type</Label>
                    <select
                      value={item.package_type}
                      onChange={(e) => updateCargoItem(index, "package_type", e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-border bg-background text-foreground"
                    >
                      {PACKAGE_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateCargoItem(index, "quantity", parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div>
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={item.weight_kg}
                      onChange={(e) => updateCargoItem(index, "weight_kg", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>HS Code</Label>
                    <Input
                      value={item.hs_code}
                      onChange={(e) => updateCargoItem(index, "hs_code", e.target.value)}
                      placeholder="e.g., 8471.30"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 cursor-pointer h-10">
                      <input
                        type="checkbox"
                        checked={item.is_hazardous}
                        onChange={(e) => updateCargoItem(index, "is_hazardous", e.target.checked)}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-foreground flex items-center gap-1">
                        <AlertCircle className="w-4 h-4 text-warning" />
                        Hazardous
                      </span>
                    </label>
                  </div>
                </div>

                {item.is_hazardous && (
                  <div className="mt-3">
                    <Label>UN Number</Label>
                    <Input
                      value={item.un_number}
                      onChange={(e) => updateCargoItem(index, "un_number", e.target.value)}
                      placeholder="e.g., UN1234"
                    />
                  </div>
                )}

                <div className="grid grid-cols-4 gap-4 mt-3">
                  <div>
                    <Label>Length (cm)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={item.length_cm}
                      onChange={(e) => updateCargoItem(index, "length_cm", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Width (cm)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={item.width_cm}
                      onChange={(e) => updateCargoItem(index, "width_cm", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Height (cm)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={item.height_cm}
                      onChange={(e) => updateCargoItem(index, "height_cm", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>or CBM</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.001"
                      value={item.volume_cbm}
                      onChange={(e) => updateCargoItem(index, "volume_cbm", e.target.value)}
                      placeholder="m³"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals for Detailed Mode */}
          <div className="mt-4 p-4 bg-background rounded-lg border border-border">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Weight className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Weight:</span>
                <span className="font-semibold text-foreground">{totalWeight.toFixed(1)} kg</span>
              </div>
              <div className="flex items-center gap-2">
                <Box className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Volume:</span>
                <span className="font-semibold text-foreground">{totalVolume.toFixed(3)} m³</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Commodity & Ready Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Commodity / Goods Type</Label>
            <Input
              value={formData.commodity}
              onChange={(e) => updateFormData("commodity", e.target.value)}
              placeholder="e.g., General merchandise, Electronics"
              data-testid="commodity"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Cargo Ready Date
            </Label>
            <Input
              type="date"
              value={formData.cargo_ready_date}
              onChange={(e) => updateFormData("cargo_ready_date", e.target.value)}
              data-testid="cargo-ready-date"
            />
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          <Button onClick={() => setStep(4)} data-testid="continue-to-services" className="gap-2">
            Continue
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Render additional services step
  const renderServicesStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Additional Services</h2>
        <p className="text-muted-foreground">Select any extra services you need</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pre-carriage */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={formData.additional_services.pre_carriage}
              onChange={(e) => updateAdditionalServices("pre_carriage", e.target.checked)}
              className="mt-1 rounded border-border"
              data-testid="pre-carriage-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-5 h-5 text-amber-500" />
                <h4 className="font-semibold text-foreground">Pre-carriage</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Collection from supplier/factory to port of loading
              </p>
              {formData.additional_services.pre_carriage && (
                <div>
                  <Label className="mb-1 block">Pickup Location</Label>
                  <SmartLocationField
                    value={{
                      city: formData.additional_services.pre_carriage_city || formData.additional_services.pre_carriage_address,
                      country: formData.additional_services.pre_carriage_country || "",
                      port_code: null,
                    }}
                    onChange={(loc) => {
                      updateAdditionalServices("pre_carriage_address", loc.city || "");
                      updateAdditionalServices("pre_carriage_city", loc.city || "");
                      updateAdditionalServices("pre_carriage_country", loc.country || "");
                    }}
                    showSeaport={true}
                    showAirport={formData.service_type === "air"}
                    placeholder="Search city or port..."
                    data-testid="pre-carriage-location"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* On-carriage */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={formData.additional_services.on_carriage}
              onChange={(e) => updateAdditionalServices("on_carriage", e.target.checked)}
              className="mt-1 rounded border-border"
              data-testid="on-carriage-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-5 h-5 text-blue-500" />
                <h4 className="font-semibold text-foreground">On-carriage</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Delivery from port of discharge to final destination
              </p>
              {formData.additional_services.on_carriage && (
                <div>
                  <Label className="mb-1 block">Delivery Location</Label>
                  <SmartLocationField
                    value={{
                      city: formData.additional_services.on_carriage_city || formData.additional_services.on_carriage_address,
                      country: formData.additional_services.on_carriage_country || "",
                      port_code: null,
                    }}
                    onChange={(loc) => {
                      updateAdditionalServices("on_carriage_address", loc.city || "");
                      updateAdditionalServices("on_carriage_city", loc.city || "");
                      updateAdditionalServices("on_carriage_country", loc.country || "");
                    }}
                    showSeaport={true}
                    showAirport={formData.service_type === "air"}
                    placeholder="Search city or port..."
                    data-testid="on-carriage-location"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customs Clearance */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={formData.additional_services.customs_clearance}
              onChange={(e) => updateAdditionalServices("customs_clearance", e.target.checked)}
              className="mt-1 rounded border-border"
              data-testid="customs-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-violet-500" />
                <h4 className="font-semibold text-foreground">Customs Clearance</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Import, export, or transit customs documentation
              </p>
              {formData.additional_services.customs_clearance && (
                <div>
                  <Label>Customs Type</Label>
                  <select
                    value={formData.additional_services.customs_type}
                    onChange={(e) => updateAdditionalServices("customs_type", e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-border bg-background text-foreground"
                    data-testid="customs-type"
                  >
                    <option value="">Select type</option>
                    <option value="import">Import Clearance</option>
                    <option value="export">Export Clearance</option>
                    <option value="transit">Transit (T1/T2)</option>
                    <option value="both">Import & Export</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Insurance */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={formData.additional_services.insurance}
              onChange={(e) => updateAdditionalServices("insurance", e.target.checked)}
              className="mt-1 rounded border-border"
              data-testid="insurance-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-emerald-500" />
                <h4 className="font-semibold text-foreground">Cargo Insurance</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Protect your cargo against loss or damage
              </p>
              {formData.additional_services.insurance && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Cargo Value</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.additional_services.insurance_value}
                      onChange={(e) => updateAdditionalServices("insurance_value", e.target.value)}
                      placeholder="Value"
                      data-testid="insurance-value"
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <select
                      value={formData.additional_services.insurance_currency}
                      onChange={(e) => updateAdditionalServices("insurance_currency", e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-border bg-background text-foreground"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Other services */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4">Other Services</h4>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.additional_services.warehousing}
                onChange={(e) => updateAdditionalServices("warehousing", e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Warehousing / Storage</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.additional_services.packaging}
                onChange={(e) => updateAdditionalServices("packaging", e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Packaging / Crating</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.additional_services.fumigation}
                onChange={(e) => updateAdditionalServices("fumigation", e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-sm text-foreground">Fumigation</span>
            </label>
          </div>
        </div>

        {/* Incoterm */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-primary" />
            <h4 className="font-semibold text-foreground">Incoterm</h4>
          </div>
          <select
            value={formData.incoterm}
            onChange={(e) => updateFormData("incoterm", e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-border bg-background text-foreground"
            data-testid="incoterm"
          >
            <option value="">Select Incoterm (optional)</option>
            {INCOTERMS.map(term => (
              <option key={term.value} value={term.value}>{term.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={() => setStep(3)} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>
        <Button onClick={() => setStep(5)} data-testid="continue-to-contact" className="gap-2">
          Continue
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Render contact & submit step
  const renderContactStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Contact Details</h2>
        <p className="text-muted-foreground">How can we reach you about this quote?</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Company Name *
              </Label>
              <Input
                value={formData.company_name}
                onChange={(e) => updateFormData("company_name", e.target.value)}
                placeholder="Your company name"
                data-testid="company-name"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Contact Name *
              </Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => updateFormData("contact_name", e.target.value)}
                placeholder="Your name"
                data-testid="contact-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email *
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData("email", e.target.value)}
                placeholder="your@email.com"
                data-testid="email"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => updateFormData("phone", e.target.value)}
                placeholder="+386 1 234 5678"
                data-testid="phone"
              />
            </div>
          </div>

          <div>
            <Label>Special Requirements / Notes</Label>
            <Textarea
              value={formData.special_requirements}
              onChange={(e) => updateFormData("special_requirements", e.target.value)}
              placeholder="Any special requirements, timing constraints, or additional information..."
              rows={4}
              data-testid="special-requirements"
            />
          </div>

          {/* Document Upload Section */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center gap-2 mb-4">
              <Upload className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-foreground">Supporting Documents</h3>
              <span className="text-xs text-muted-foreground">(Optional)</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Upload documents such as commercial invoices, packing lists, or MSDS for dangerous goods.
            </p>

            {/* Uploaded Documents List */}
            {documents.length > 0 && (
              <div className="space-y-2 mb-4">
                {documents.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between p-3 bg-surface border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <File className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {DOCUMENT_TYPES.find(d => d.value === doc.type)?.label} • {(doc.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleRemoveDocument(doc.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Document Type Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {DOCUMENT_TYPES.map((docType) => {
                const isUploaded = documents.some(d => d.type === docType.value);
                const isDangerous = formData.cargo_items.some(item => item.is_hazardous);
                const isMsdsHighlighted = docType.value === 'msds' && isDangerous && !isUploaded;
                
                return (
                  <div key={docType.value} className="relative">
                    <input
                      type="file"
                      id={`doc-upload-${docType.value}`}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                      onChange={(e) => handleDocumentUpload(e, docType.value)}
                      disabled={uploadingDoc}
                    />
                    <label
                      htmlFor={`doc-upload-${docType.value}`}
                      className={`
                        flex flex-col items-center justify-center p-3 rounded-lg border-2 border-dashed 
                        cursor-pointer transition-all text-center min-h-[80px]
                        ${isUploaded 
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' 
                          : isMsdsHighlighted
                            ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20 animate-pulse'
                            : 'border-border hover:border-primary hover:bg-accent/50'
                        }
                      `}
                    >
                      {isUploaded ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mb-1" />
                      ) : isMsdsHighlighted ? (
                        <AlertTriangle className="w-5 h-5 text-amber-500 mb-1" />
                      ) : (
                        <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                      )}
                      <span className={`text-xs font-medium ${isUploaded ? 'text-emerald-600' : isMsdsHighlighted ? 'text-amber-600' : 'text-foreground'}`}>
                        {docType.label}
                      </span>
                      {isMsdsHighlighted && (
                        <span className="text-[10px] text-amber-600 mt-0.5">Required for DG</span>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>

            {uploadingDoc && (
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading document...
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-3">
              Accepted formats: PDF, Word, Excel, Images. Max size: 10MB per file.
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-surface border border-border rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quote Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Service:</span>
              <span className="text-foreground font-medium">{SERVICE_TYPES.find(s => s.id === selectedService)?.fullName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Route:</span>
              <span className="text-foreground font-medium">
                {formData.origin_port_airport || "Origin"} → {formData.destination_port_airport || "Destination"}
              </span>
            </div>
            {selectedService === "fcl" && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Containers:</span>
                <span className="text-foreground font-medium">{formData.container_quantity}x {formData.container_type}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cargo Items:</span>
              <span className="text-foreground font-medium">{formData.cargo_items.filter(i => i.description).length}</span>
            </div>
            {formData.additional_services.pre_carriage && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pre-carriage:</span>
                <span className="text-foreground font-medium">Yes</span>
              </div>
            )}
            {formData.additional_services.on_carriage && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">On-carriage:</span>
                <span className="text-foreground font-medium">Yes</span>
              </div>
            )}
            {formData.additional_services.customs_clearance && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customs:</span>
                <span className="text-foreground font-medium capitalize">{formData.additional_services.customs_type || "Yes"}</span>
              </div>
            )}
            {formData.additional_services.insurance && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Insurance:</span>
                <span className="text-foreground font-medium">Yes</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={() => setStep(4)} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !formData.company_name || !formData.contact_name || !formData.email}
          data-testid="submit-quote"
          className="gap-2"
        >
          {submitting ? (
            <>
              <Clock className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit Quote Request
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Render success state
  const renderSuccess = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-12"
    >
      <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-emerald-500" />
      </div>
      <h2 className="text-2xl font-semibold text-foreground mb-2">Quote Request Submitted!</h2>
      <p className="text-muted-foreground mb-4">
        Your quote number is: <span className="font-mono font-semibold text-primary">{quoteNumber}</span>
      </p>
      <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
        Our team will review your request and get back to you with a competitive quote within 24-48 hours.
      </p>
      <Button onClick={resetForm} data-testid="submit-another" className="gap-2">
        <Plus className="w-4 h-4" />
        Submit Another Request
      </Button>
    </motion.div>
  );

  // Step indicator
  const renderStepIndicator = () => (
    <div className="flex flex-col items-center mb-8">
      <div className="flex items-center justify-center gap-2 mb-4">
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                s === step
                  ? "bg-primary text-primary-foreground"
                  : s < step
                  ? "bg-primary/20 text-primary"
                  : "bg-surface border border-border text-muted-foreground"
              }`}
            >
              {s < step ? <CheckCircle className="w-4 h-4" /> : s}
            </div>
            {s < 5 && (
              <div className={`w-8 h-0.5 ${s < step ? "bg-primary" : "bg-border"}`} />
            )}
          </div>
        ))}
      </div>
      {/* Save Draft Button */}
      {token && step > 1 && !submitted && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveDraft}
          disabled={savingDraft}
          className="gap-2 text-amber-600 border-amber-200 hover:bg-amber-50"
          data-testid="save-draft-btn"
        >
          {savingDraft ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save as Draft
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-foreground mb-2">Get a Quote</h1>
            <p className="text-muted-foreground">
              Request a competitive freight quote from Europacific
            </p>
          </motion.div>

          {/* Draft Banner */}
          {showDraftBanner && availableDrafts.length > 0 && !submitted && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-1">You have saved drafts</h3>
                    <p className="text-sm text-amber-700 mb-3">
                      Continue where you left off or start a new quote request.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {availableDrafts.map((draft) => (
                        <div key={draft.id} className="flex items-center gap-1 bg-white border border-amber-200 rounded-lg px-3 py-1.5">
                          <span className="text-sm font-medium text-amber-800 uppercase">
                            {draft.service_type || "Quote"} 
                          </span>
                          <span className="text-xs text-amber-600">
                            (Step {draft.current_step}/5)
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadDraft(draft)}
                            className="h-6 px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                            data-testid={`load-draft-${draft.id}`}
                          >
                            Load
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteDraft(draft.id)}
                            className="h-6 px-1 text-amber-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-draft-${draft.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowDraftBanner(false)}
                  className="text-amber-500 hover:text-amber-700 hover:bg-amber-100 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          <div className="bg-surface/50 border border-border rounded-xl p-6 md:p-8">
            {!submitted && renderStepIndicator()}
            
            <AnimatePresence mode="wait">
              {submitted ? (
                renderSuccess()
              ) : (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {step === 1 && renderServiceSelection()}
                  {step === 2 && renderRoutingStep()}
                  {step === 3 && renderCargoStep()}
                  {step === 4 && renderServicesStep()}
                  {step === 5 && renderContactStep()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
